import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Pagination, Button, Row, message } from "antd";
import axios from "axios";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import { formatCardSummary } from "../../helpers/cardBinding";
import { CARD_CHARGE_STATUS_COLORS, CARD_HISTORY_PAGE_SIZE } from "../../constants/cardBinding";

const cardChargeColumns = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (text) => (text ? formatDateTime(text) : "-"),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (amount) => formatAmount(amount, "RM"),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => <Tag color={CARD_CHARGE_STATUS_COLORS[status]}>{status}</Tag>,
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
  },
  {
    title: "Card",
    key: "card",
    render: (record) => formatCardSummary(record),
  },
  {
    title: "Failure Reason",
    dataIndex: "failureReason",
    key: "failureReason",
    render: (text) => text || "-",
  },
  {
    title: "Charged By",
    dataIndex: "chargedBy",
    key: "chargedBy",
  },
  {
    title: "Settled At",
    dataIndex: "settledAt",
    key: "settledAt",
    render: (text) => (text ? formatDateTime(text) : "-"),
  },
];

const emailColumn = {
  title: "Email",
  dataIndex: "merchantEmail",
  key: "merchantEmail",
  render: (email) => email || "-",
};

// allMerchants lists every merchant's charges, with an Email column, instead of one merchant's.
// filters ({ email, status }) are sent as query params; changing them reloads from page 1.
// refreshKey changes after a new charge so the list reloads from page 1
const UserCardChargeHistory = ({
  merchantId,
  allMerchants = false,
  filters,
  refreshKey,
  pageSize = CARD_HISTORY_PAGE_SIZE,
  title = "Charge History",
}) => {
  const [charges, setCharges] = useState([]);
  // token forces a refetch even when the page itself does not change
  const [query, setQuery] = useState({ page: 1, token: 0, filters });
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(false);

  const endpoint = allMerchants ? "/api/card-charges" : `/api/merchants/${merchantId}/card-charges`;
  const columns = allMerchants
    ? [cardChargeColumns[0], emailColumn, ...cardChargeColumns.slice(1)]
    : cardChargeColumns;

  // A newer query aborts the pending request, so a slow stale response cannot overwrite newer results
  useEffect(() => {
    if (!allMerchants && !merchantId) return undefined;
    const controller = new AbortController();
    const fetchCharges = async () => {
      setLoading(true);
      try {
        // A merchant's history also queries Paydibs for pending charges on the page, so it can be slow
        const res = await axios.get(endpoint, {
          params: { page: query.page, limit: pageSize, ...query.filters },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setCharges(res.data.data);
        setTotalSize(res.data.meta?.totalSize || 0);
      } catch (error) {
        if (axios.isCancel(error)) return;
        setCharges([]);
        setTotalSize(0);
        message.error(error.response?.data?.message || error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchCharges();
    return () => controller.abort();
  }, [allMerchants, merchantId, endpoint, pageSize, query]);

  useEffect(() => {
    if (!refreshKey) return;
    setQuery((prev) => ({ ...prev, page: 1, token: prev.token + 1 }));
  }, [refreshKey]);

  useEffect(() => {
    setQuery((prev) => (prev.filters === filters ? prev : { ...prev, page: 1, filters }));
  }, [filters]);

  return (
    <Card
      size="small"
      title={title}
      className="mb-2"
      extra={
        <Button
          size="small"
          loading={loading}
          onClick={() => setQuery((prev) => ({ ...prev, token: prev.token + 1 }))}
        >
          Refresh
        </Button>
      }
    >
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={charges}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
      />
      <Row justify="end" className="mt-2">
        <Pagination
          simple
          current={query.page}
          pageSize={pageSize}
          total={totalSize}
          onChange={(page) => setQuery((prev) => ({ ...prev, page }))}
        />
      </Row>
    </Card>
  );
};

export default UserCardChargeHistory;
